import {
    useAvailableEventLocations,
    useAvailableEventLocationsById,
    useEventLocationDetails,
} from 'hooks/user/useEvents/useEventLocationsInUse'
import { useMemo } from 'react'

import { useCurrentAccount } from '../useAuth'
import { useLocationById, useLocationsAdmin } from './useLocations'

interface UseLocationsDataProps {
    selectedRow: any
    eventId?: string
    partnerId?: string
}

export const useLocationsWizard = ({ selectedRow, eventId, partnerId }: UseLocationsDataProps) => {
    const { currentAccount, isLoadingCurrentAccount, errorCurrentAccount } = useCurrentAccount()
    const { tableConfig } = useLocationsAdmin(partnerId)

    const {
        data: availableEventLocations,
        isLoading: isAvailableEventLocationsLoading,
        error: availableEventLocationsError,
    } = useAvailableEventLocations()

    const {
        data: availableEventLocationsById,
        isLoading: isAvailableEventLocationsByIdLoading,
        error: availableEventLocationsByIdError,
    } = useAvailableEventLocationsById(eventId || '')

    const {
        data: eventLocationDetails,
        isLoading: isEventLocationDetailsLoading,
        error: eventLocationDetailsError,
    } = useEventLocationDetails(selectedRow?.event_location_id || '')

    const { data: locationData, isLoading: isLocationLoading, error: locationError } = useLocationById(selectedRow?.location_id || '')

    const eventLocations = useMemo(() => {
        let locations =
            eventId && !isAvailableEventLocationsByIdLoading && !availableEventLocationsByIdError
                ? availableEventLocationsById
                : !eventId && !isAvailableEventLocationsLoading && !availableEventLocationsError
                  ? availableEventLocations
                  : undefined

        // If locations exist, filter them based on location_id and event_location_id
        if (locations) {
            // Create a map of unique location_ids, prioritizing the one with an event_location_id
            const filteredLocations = locations.reduce((acc: any[], loc: any) => {
                const existing = acc.find((item) => item.location_id === loc.location_id)

                if (!existing || (loc.event_location_id && !existing.event_location_id)) {
                    // If the location_id is not yet in the list or the current entry has an event_location_id,
                    // replace the existing entry
                    return [...acc.filter((item) => item.location_id !== loc.location_id), loc]
                }
                return acc
            }, [])

            return filteredLocations
        }

        return locations
    }, [
        eventId,
        availableEventLocationsById,
        isAvailableEventLocationsByIdLoading,
        availableEventLocationsByIdError,
        availableEventLocations,
        isAvailableEventLocationsLoading,
        availableEventLocationsError,
    ])

    const dataToDisplay = useMemo(() => {
        console.log('selectedRow', selectedRow)
        if (selectedRow?.event_location_id && eventLocationDetails) {
            console.log('eventLocationDetails', eventLocationDetails)
            return { ...eventLocationDetails, type: 'event_location' }
        }
        if (selectedRow?.location_id && locationData) {
            return { ...locationData, type: 'location' }
        }

        return null
    }, [selectedRow, eventLocationDetails, locationData])

    const idName = useMemo(() => {
        if (selectedRow?.event_location_id) {
            return 'event_location_id'
        }
        if (selectedRow?.location_id) {
            return 'location_id'
        }
        return tableConfig.idName
    }, [selectedRow, tableConfig.idName])

    const isLoading =
        isAvailableEventLocationsLoading ||
        isAvailableEventLocationsByIdLoading ||
        isLoadingCurrentAccount ||
        isLocationLoading ||
        isEventLocationDetailsLoading
    const error =
        availableEventLocationsError ||
        availableEventLocationsByIdError ||
        errorCurrentAccount ||
        locationError ||
        eventLocationDetailsError

    return { currentAccount, eventLocations, dataToDisplay, isLoading, error, tableConfig: { ...tableConfig, idName } }
}
